import { useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Save } from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await settingsAPI.get();
      setSettings(data);
      setFormData(data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching settings:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await settingsAPI.update(formData);
      alert('Settings saved successfully!');
      fetchSettings();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error saving settings:', error);
      }
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8"><p className="font-manrope text-gray-600">Loading settings...</p></div>;
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="font-barlow text-4xl font-bold uppercase mb-2">Settings</h1>
        <p className="font-manrope text-gray-600">Configure your API integrations</p>
      </div>

      <form onSubmit={handleSave}>
        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000] mb-6">
          <CardHeader>
            <CardTitle className="font-barlow text-xl uppercase">Twilio (SMS)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block font-manrope font-semibold mb-2">Account SID</label>
              <Input
                value={formData.twilio_sid || ''}
                onChange={(e) => setFormData({...formData, twilio_sid: e.target.value})}
                className="border-2 border-black"
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              />
            </div>
            <div>
              <label className="block font-manrope font-semibold mb-2">Auth Token</label>
              <Input
                type="password"
                value={formData.twilio_token || ''}
                onChange={(e) => setFormData({...formData, twilio_token: e.target.value})}
                className="border-2 border-black"
                placeholder="Your auth token"
              />
            </div>
            <div>
              <label className="block font-manrope font-semibold mb-2">Phone Number</label>
              <Input
                value={formData.twilio_phone || ''}
                onChange={(e) => setFormData({...formData, twilio_phone: e.target.value})}
                className="border-2 border-black"
                placeholder="+1234567890"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000] mb-6">
          <CardHeader>
            <CardTitle className="font-barlow text-xl uppercase">SendGrid (Email)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block font-manrope font-semibold mb-2">API Key</label>
              <Input
                type="password"
                value={formData.sendgrid_key || ''}
                onChange={(e) => setFormData({...formData, sendgrid_key: e.target.value})}
                className="border-2 border-black"
                placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxx"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000] mb-6">
          <CardHeader>
            <CardTitle className="font-barlow text-xl uppercase">Facebook</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block font-manrope font-semibold mb-2">Page Access Token</label>
              <Input
                type="password"
                value={formData.facebook_token || ''}
                onChange={(e) => setFormData({...formData, facebook_token: e.target.value})}
                className="border-2 border-black"
                placeholder="EAAxxxxxxxxxxxxxxxxxxxxxxxxx"
              />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000] mb-6">
          <CardHeader>
            <CardTitle className="font-barlow text-xl uppercase">Google Maps</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block font-manrope font-semibold mb-2">API Key</label>
              <Input
                type="password"
                value={formData.google_maps_key || ''}
                onChange={(e) => setFormData({...formData, google_maps_key: e.target.value})}
                className="border-2 border-black"
                placeholder="AIzaSyXXXXXXXXXXXXXXXXXXXXXX"
              />
              <p className="text-sm text-gray-500 mt-2">
                Used for enhanced roof measurement accuracy
              </p>
            </div>
          </CardContent>
        </Card>

        <Button
          type="submit"
          disabled={saving}
          className="bg-primary hover:bg-primary-hover text-white border-2 border-black shadow-[4px_4px_0px_0px_#000]"
        >
          <Save className="mr-2 h-5 w-5" />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </form>

      <Card className="border-2 border-black shadow-[4px_4px_0px_0px_#000] mt-8 bg-yellow-50">
        <CardContent className="pt-6">
          <p className="font-manrope text-sm text-gray-700">
            <strong>Note:</strong> API keys are stored securely. You can add them later when you're ready to enable auto-responses and social media posting.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
